<template>
  <form @submit="onSubmit">
    <BaseCard
      class="relative mx-auto w-full max-w-sm overflow-hidden"
    >
      <div class="space-y-6">
        <div
          class="col-span-4 flex w-full items-center justify-center"
        >
          <div
            class="relative flex size-12 items-center justify-center rounded border border-background-fg/10 bg-primary font-semibold text-primary-fg"
            :style="{
              backgroundColor: values.color || undefined,
            }"
          >
            {{ buildShortString(values.name ?? "") }}
          </div>
        </div>
        <FormInputText name="name" label="Category Name" />

        <FormInputColor
          name="color"
          label="Display Color"
        />

        <FormInputSelect
          :options="categoryOptions"
          label="Parent Category"
          name="parentCategoryId"
        />

        <FormSubmitButton
          class="w-full"
          :is-submitting="isSubmitting"
        >
          Save Category
        </FormSubmitButton>
      </div>

      <BaseInnerLoading
        v-if="categoryId && isRetrievingCategory"
      />
    </BaseCard>
  </form>

  <ErrorModal
    :is-open="isErrorModalOpen"
    message="Could not retrieve category"
    description="Something went wrong fetching category"
    :go-back-to="{
      name: 'categoryIndex',
    }"
    go-back-label="Return to category list"
  />
</template>

<script setup lang="ts">
import { ApiError } from "@/api"

const props = defineProps<{
  categoryId?: string
}>()

const router = useRouter()

const { data: categories } = useCategories()

const categoryOptions = computed(() => {
  const updatingId = props.categoryId
  return (
    updatingId
      ? categories.value.filter(
          ({ id, path }) =>
            id !== updatingId && !path.includes(updatingId)
        )
      : categories.value
  ).map(({ id, namePath }) => ({
    value: id,
    label: namePath,
  }))
})

const {
  handleSubmit,
  values,
  setErrors,
  setValues,
  isSubmitting,
} = useCreateUpdateCategoryForm()

const onSubmit = handleSubmit(async (formValues) => {
  try {
    await (props.categoryId
      ? updateCategory(props.categoryId, { ...formValues })
      : createCategory(formValues))
    toast.success(
      props.categoryId
        ? "Category updated"
        : "Category created"
    )
    router.push({ name: "categoryIndex" })
  } catch (error) {
    toast.error(
      props.categoryId
        ? "Failed to update category"
        : "Failed to create category"
    )

    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    } else {
      throw error
    }
  }
})

const isErrorModalOpen = ref(false)
const isRetrievingCategory = ref(false)
onMounted(async () => {
  if (props.categoryId !== undefined) {
    try {
      isRetrievingCategory.value = true
      const category = await retrieveCategory(
        props.categoryId
      )
      setValues(category)
    } catch {
      isErrorModalOpen.value = true
    } finally {
      isRetrievingCategory.value = false
    }
  }
})
</script>
